import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Trophy, Users, Calendar, DollarSign, Eye, Edit } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LeagueTab = ({
  teams,
  matches,
  salons,
  personnelList,
  fetchTeams,
  fetchMatches,
  fetchIncomes,
  fetchExpenses
}) => {
  const [activeTab, setActiveTab] = useState('matches');
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [showAddMatchModal, setShowAddMatchModal] = useState(false);
  const [showTeamDetailModal, setShowTeamDetailModal] = useState(false);
  const [showMatchDetailModal, setShowMatchDetailModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [teamMatches, setTeamMatches] = useState([]);
  const [teamPayments, setTeamPayments] = useState([]);
  const [matchPersonnel, setMatchPersonnel] = useState([]);
  const [selectedPersonnelIds, setSelectedPersonnelIds] = useState([]);

  // Form states
  const [newTeam, setNewTeam] = useState({
    name: '',
    captain: '',
    phone: ''
  });

  const [newMatch, setNewMatch] = useState({
    home_team_id: '',
    away_team_id: '',
    date: new Date().toISOString().split('T')[0],
    time: '20:00',
    week: 1,
    salon_id: '',
    match_type: 'lig_maci',
    home_team_payment_amount: 0,
    home_team_payment_status: 'odenmedi',
    home_team_payment_method: 'nakit',
    away_team_payment_amount: 0,
    away_team_payment_status: 'odenmedi',
    away_team_payment_method: 'nakit',
    notes: '',
    personnel_ids: []
  });

  // ============ TEAM FUNCTIONS ============
  const createTeam = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/teams`, newTeam);
      toast.success('Takım eklendi');
      setNewTeam({ name: '', captain: '', phone: '' });
      setShowAddTeamModal(false);
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Takım eklenemedi');
    }
  };

  const deleteTeam = async (teamId) => {
    if (!window.confirm('Bu takımı silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/teams/${teamId}`);
      toast.success('Takım silindi');
      fetchTeams();
      fetchMatches();
    } catch (error) {
      toast.error('Takım silinemedi');
    }
  };

  const openTeamDetail = async (team) => {
    setSelectedTeam(team);
    // Takımın maçlarını getir
    const teamMatchList = matches.filter(
      m => m.home_team_id === team.id || m.away_team_id === team.id
    );
    setTeamMatches(teamMatchList);
    
    // Ödeme geçmişini hazırla
    const payments = [];
    teamMatchList.forEach(match => {
      const isHome = match.home_team_id === team.id;
      const opponentId = isHome ? match.away_team_id : match.home_team_id;
      const opponent = teams.find(t => t.id === opponentId);
      
      if (isHome && match.home_team_payment_amount > 0) {
        payments.push({
          date: match.date,
          match: `vs ${opponent?.name || 'Bilinmiyor'}`,
          amount: match.home_team_payment_amount,
          status: match.home_team_payment_status,
          method: match.home_team_payment_method
        });
      } else if (!isHome && match.away_team_payment_amount > 0) {
        payments.push({
          date: match.date,
          match: `vs ${opponent?.name || 'Bilinmiyor'}`,
          amount: match.away_team_payment_amount,
          status: match.away_team_payment_status,
          method: match.away_team_payment_method
        });
      }
    });
    setTeamPayments(payments);
    setShowTeamDetailModal(true);
  };

  // ============ MATCH FUNCTIONS ============
  const createMatch = async (e) => {
    e.preventDefault();
    
    if (newMatch.home_team_id === newMatch.away_team_id) {
      toast.error('Ev sahibi ve deplasman takımı aynı olamaz');
      return;
    }

    try {
      const matchData = {
        home_team_id: newMatch.home_team_id,
        away_team_id: newMatch.away_team_id,
        date: newMatch.date,
        time: newMatch.time,
        week: parseInt(newMatch.week),
        salon_id: newMatch.salon_id || null,
        match_type: newMatch.match_type,
        home_team_payment_amount: parseFloat(newMatch.home_team_payment_amount),
        away_team_payment_amount: parseFloat(newMatch.away_team_payment_amount),
        notes: newMatch.notes
      };

      const response = await axios.post(`${API}/matches`, matchData);
      const matchId = response.data.id;

      // Ödemeler otomatik olarak kasaya kaydedilecek (backend'de)
      if (newMatch.home_team_payment_status === 'odendi') {
        await axios.put(`${API}/matches/${matchId}/payment`, {
          team_type: 'home',
          payment_status: 'odendi',
          payment_method: newMatch.home_team_payment_method,
          payment_amount: parseFloat(newMatch.home_team_payment_amount)
        });
      }

      if (newMatch.away_team_payment_status === 'odendi') {
        await axios.put(`${API}/matches/${matchId}/payment`, {
          team_type: 'away',
          payment_status: 'odendi',
          payment_method: newMatch.away_team_payment_method,
          payment_amount: parseFloat(newMatch.away_team_payment_amount)
        });
      }

      // Personel ataması
      if (newMatch.personnel_ids.length > 0) {
        for (const personnelId of newMatch.personnel_ids) {
          await axios.post(`${API}/match-personnel`, {
            match_id: matchId,
            personnel_id: personnelId,
            role: 'saha_gorevlisi'
          });
        }
      }

      toast.success('Maç oluşturuldu');
      setShowAddMatchModal(false);
      setNewMatch({
        home_team_id: '',
        away_team_id: '',
        date: new Date().toISOString().split('T')[0],
        time: '20:00',
        week: 1,
        salon_id: '',
        match_type: 'lig_maci',
        home_team_payment_amount: 0,
        home_team_payment_status: 'odenmedi',
        home_team_payment_method: 'nakit',
        away_team_payment_amount: 0,
        away_team_payment_status: 'odenmedi',
        away_team_payment_method: 'nakit',
        notes: '',
        personnel_ids: []
      });
      fetchMatches();
      fetchIncomes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Maç oluşturulamadı');
    }
  };

  const deleteMatch = async (matchId) => {
    if (!window.confirm('Bu maçı silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/matches/${matchId}`);
      toast.success('Maç silindi');
      fetchMatches();
      fetchIncomes();
    } catch (error) {
      toast.error('Maç silinemedi');
    }
  };

  const updateMatchPayment = async (matchId, teamType, paymentData) => {
    try {
      await axios.put(`${API}/matches/${matchId}/payment`, {
        team_type: teamType,
        ...paymentData
      });
      toast.success('Ödeme güncellendi');
      fetchMatches();
      fetchIncomes();
    } catch (error) {
      toast.error('Ödeme güncellenemedi');
    }
  };

  const updateMatchScore = async (matchId, homeScore, awayScore) => {
    try {
      await axios.put(`${API}/matches/${matchId}`, {
        home_score: parseInt(homeScore),
        away_score: parseInt(awayScore),
        status: 'tamamlandi'
      });
      toast.success('Skor güncellendi');
      fetchMatches();
    } catch (error) {
      toast.error('Skor güncellenemedi');
    }
  };

  // ============ PERSONNEL FUNCTIONS ============
  const fetchMatchPersonnel = async (matchId) => {
    try {
      const response = await axios.get(`${API}/match-personnel/${matchId}`);
      setMatchPersonnel(response.data);
      setSelectedPersonnelIds(response.data.map(p => p.personnel_id));
    } catch (error) {
      console.error('Personel bilgisi alınamadı:', error);
      setMatchPersonnel([]);
      setSelectedPersonnelIds([]);
    }
  };

  const addPersonnelToMatch = async (matchId, personnelId) => {
    try {
      await axios.post(`${API}/match-personnel`, {
        match_id: matchId,
        personnel_id: personnelId,
        role: 'saha_gorevlisi'
      });
      toast.success('Personel eklendi');
      fetchMatchPersonnel(matchId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Personel eklenemedi');
    }
  };

  const removePersonnelFromMatch = async (assignmentId, matchId) => {
    try {
      await axios.delete(`${API}/match-personnel/${assignmentId}`);
      toast.success('Personel çıkarıldı');
      fetchMatchPersonnel(matchId);
    } catch (error) {
      toast.error('Personel çıkarılamadı');
    }
  };

  const openMatchDetail = (match) => {
    setSelectedMatch(match);
    fetchMatchPersonnel(match.id);
    setShowMatchDetailModal(true);
  };

  // ============ STATISTICS ============
  const stats = {
    totalTeams: teams.length,
    totalMatches: matches.length,
    completedMatches: matches.filter(m => m.status === 'tamamlandi').length,
    totalRevenue: matches.reduce((sum, m) => {
      const homeRevenue = m.home_team_payment_status === 'odendi' ? m.home_team_payment_amount : 0;
      const awayRevenue = m.away_team_payment_status === 'odendi' ? m.away_team_payment_amount : 0;
      return sum + homeRevenue + awayRevenue;
    }, 0),
    pendingPayments: matches.reduce((sum, m) => {
      const homePending = m.home_team_payment_status !== 'odendi' ? m.home_team_payment_amount : 0;
      const awayPending = m.away_team_payment_status !== 'odendi' ? m.away_team_payment_amount : 0;
      return sum + homePending + awayPending;
    }, 0)
  };

  // Helper functions
  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team?.name || 'Bilinmiyor';
  };

  const getSalonName = (salonId) => {
    if (!salonId) return '-';
    const salon = salons.find(s => s.id === salonId);
    return salon?.name || '-';
  };

  const getPaymentStatusBadge = (status) => {
    const variants = {
      'odendi': 'bg-green-500 text-white',
      'odenmedi': 'bg-red-500 text-white',
      'kismi': 'bg-yellow-500 text-white'
    };
    const labels = {
      'odendi': 'Ödendi',
      'odenmedi': 'Ödenmedi',
      'kismi': 'Kısmi'
    };
    return <Badge className={variants[status]}>{labels[status]}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8" />
              <div>
                <p className="text-sm opacity-90">Toplam Takım</p>
                <p className="text-2xl font-bold">{stats.totalTeams}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Trophy className="w-8 h-8" />
              <div>
                <p className="text-sm opacity-90">Toplam Maç</p>
                <p className="text-2xl font-bold">{stats.totalMatches}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Calendar className="w-8 h-8" />
              <div>
                <p className="text-sm opacity-90">Oynan</p>
                <p className="text-2xl font-bold">{stats.completedMatches}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-500 to-orange-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <DollarSign className="w-8 h-8" />
              <div>
                <p className="text-sm opacity-90">Gelir</p>
                <p className="text-2xl font-bold">₺{stats.totalRevenue.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-rose-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <DollarSign className="w-8 h-8" />
              <div>
                <p className="text-sm opacity-90">Bekleyen</p>
                <p className="text-2xl font-bold">₺{stats.pendingPayments.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-white p-1 rounded-lg shadow-sm">
        <Button
          variant={activeTab === 'matches' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('matches')}
          className="flex-1"
        >
          <Trophy className="w-4 h-4 mr-2" />
          Maçlar
        </Button>
        <Button
          variant={activeTab === 'teams' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('teams')}
          className="flex-1"
        >
          <Users className="w-4 h-4 mr-2" />
          Takımlar
        </Button>
      </div>

      {/* Content */}
      {activeTab === 'matches' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">Maçlar</h2>
            <Button onClick={() => setShowAddMatchModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Yeni Maç
            </Button>
          </div>

          <div className="grid gap-4">
            {matches.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  Henüz maç eklenmemiş
                </CardContent>
              </Card>
            ) : (
              matches.map((match) => (
                <Card key={match.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex flex-col md:flex-row justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-2">
                          <Trophy className="w-5 h-5 text-blue-500" />
                          <span className="font-bold text-lg">
                            {getTeamName(match.home_team_id)} vs {getTeamName(match.away_team_id)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div>📅 {new Date(match.date).toLocaleDateString('tr-TR')} - {match.time}</div>
                          <div>🏢 {getSalonName(match.salon_id)}</div>
                          <div>📊 Hafta {match.week}</div>
                          {match.home_score !== null && (
                            <div className="text-lg font-bold text-blue-600">
                              Skor: {match.home_score} - {match.away_score}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="text-sm">
                          <span className="font-semibold">Ev Sahibi:</span>{' '}
                          {getPaymentStatusBadge(match.home_team_payment_status)}
                          <span className="ml-2">₺{match.home_team_payment_amount}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-semibold">Deplasman:</span>{' '}
                          {getPaymentStatusBadge(match.away_team_payment_status)}
                          <span className="ml-2">₺{match.away_team_payment_amount}</span>
                        </div>
                        <div className="flex gap-2 mt-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openMatchDetail(match)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Detay
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteMatch(match.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'teams' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">Takımlar</h2>
            <Button onClick={() => setShowAddTeamModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Yeni Takım
            </Button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {teams.length === 0 ? (
              <Card className="col-span-full">
                <CardContent className="py-12 text-center text-gray-500">
                  Henüz takım eklenmemiş
                </CardContent>
              </Card>
            ) : (
              teams.map((team) => {
                const teamMatchCount = matches.filter(
                  m => m.home_team_id === team.id || m.away_team_id === team.id
                ).length;
                const teamRevenue = matches.reduce((sum, m) => {
                  if (m.home_team_id === team.id && m.home_team_payment_status === 'odendi') {
                    return sum + m.home_team_payment_amount;
                  }
                  if (m.away_team_id === team.id && m.away_team_payment_status === 'odendi') {
                    return sum + m.away_team_payment_amount;
                  }
                  return sum;
                }, 0);

                return (
                  <Card key={team.id} className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => openTeamDetail(team)}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-blue-500" />
                        {team.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="text-sm text-gray-600">
                        <div>👤 Kaptan: {team.captain}</div>
                        <div>📞 {team.phone || 'Telefon yok'}</div>
                        <div className="mt-3 pt-3 border-t">
                          <div>⚽ Maç Sayısı: <span className="font-bold">{teamMatchCount}</span></div>
                          <div>💰 Ödenen: <span className="font-bold text-green-600">₺{teamRevenue.toLocaleString()}</span></div>
                        </div>
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            openTeamDetail(team);
                          }}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Detay
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteTeam(team.id);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Add Team Modal */}
      <Dialog open={showAddTeamModal} onOpenChange={setShowAddTeamModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Yeni Takım Ekle</DialogTitle>
          </DialogHeader>
          <form onSubmit={createTeam} className="space-y-4">
            <div>
              <Label>Takım Adı *</Label>
              <Input
                value={newTeam.name}
                onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Kaptan Adı *</Label>
              <Input
                value={newTeam.captain}
                onChange={(e) => setNewTeam({ ...newTeam, captain: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Kaptan Telefonu *</Label>
              <Input
                value={newTeam.phone}
                onChange={(e) => setNewTeam({ ...newTeam, phone: e.target.value })}
                placeholder="05XX XXX XX XX"
                required
              />
            </div>
            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddTeamModal(false)} className="flex-1">
                İptal
              </Button>
              <Button type="submit" className="flex-1">Takım Ekle</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Match Modal */}
      <Dialog open={showAddMatchModal} onOpenChange={setShowAddMatchModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Yeni Maç Ekle</DialogTitle>
          </DialogHeader>
          <form onSubmit={createMatch} className="space-y-6">
            {/* Takımlar */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Ev Sahibi Takım *</Label>
                <Select
                  value={newMatch.home_team_id}
                  onValueChange={(value) => setNewMatch({ ...newMatch, home_team_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Takım seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.map(team => (
                      <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Deplasman Takımı *</Label>
                <Select
                  value={newMatch.away_team_id}
                  onValueChange={(value) => setNewMatch({ ...newMatch, away_team_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Takım seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.map(team => (
                      <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Tarih ve Saat */}
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Tarih *</Label>
                <Input
                  type="date"
                  value={newMatch.date}
                  onChange={(e) => setNewMatch({ ...newMatch, date: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Saat *</Label>
                <Input
                  type="time"
                  value={newMatch.time}
                  onChange={(e) => setNewMatch({ ...newMatch, time: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Hafta *</Label>
                <Input
                  type="number"
                  value={newMatch.week}
                  onChange={(e) => setNewMatch({ ...newMatch, week: e.target.value })}
                  min="1"
                  required
                />
              </div>
            </div>

            {/* Salon */}
            <div>
              <Label>Salon</Label>
              <Select
                value={newMatch.salon_id}
                onValueChange={(value) => setNewMatch({ ...newMatch, salon_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Salon seçin (opsiyonel)" />
                </SelectTrigger>
                <SelectContent>
                  {salons.map(salon => (
                    <SelectItem key={salon.id} value={salon.id}>{salon.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Ev Sahibi Ödeme */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Ev Sahibi Takım Ödemesi</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Tutar (₺) *</Label>
                  <Input
                    type="number"
                    value={newMatch.home_team_payment_amount}
                    onChange={(e) => setNewMatch({ ...newMatch, home_team_payment_amount: e.target.value })}
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                <div>
                  <Label>Ödeme Durumu</Label>
                  <Select
                    value={newMatch.home_team_payment_status}
                    onValueChange={(value) => setNewMatch({ ...newMatch, home_team_payment_status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="odendi">Ödendi</SelectItem>
                      <SelectItem value="odenmedi">Ödenmedi</SelectItem>
                      <SelectItem value="kismi">Kısmi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Ödeme Yöntemi</Label>
                  <Select
                    value={newMatch.home_team_payment_method}
                    onValueChange={(value) => setNewMatch({ ...newMatch, home_team_payment_method: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nakit">Nakit</SelectItem>
                      <SelectItem value="kart">Kart</SelectItem>
                      <SelectItem value="havale">Havale</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Deplasman Ödeme */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Deplasman Takımı Ödemesi</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Tutar (₺) *</Label>
                  <Input
                    type="number"
                    value={newMatch.away_team_payment_amount}
                    onChange={(e) => setNewMatch({ ...newMatch, away_team_payment_amount: e.target.value })}
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                <div>
                  <Label>Ödeme Durumu</Label>
                  <Select
                    value={newMatch.away_team_payment_status}
                    onValueChange={(value) => setNewMatch({ ...newMatch, away_team_payment_status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="odendi">Ödendi</SelectItem>
                      <SelectItem value="odenmedi">Ödenmedi</SelectItem>
                      <SelectItem value="kismi">Kısmi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Ödeme Yöntemi</Label>
                  <Select
                    value={newMatch.away_team_payment_method}
                    onValueChange={(value) => setNewMatch({ ...newMatch, away_team_payment_method: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nakit">Nakit</SelectItem>
                      <SelectItem value="kart">Kart</SelectItem>
                      <SelectItem value="havale">Havale</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Personel Atama */}
            <div>
              <Label>Personel Atama (Opsiyonel)</Label>
              <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto border rounded p-2">
                {personnelList.map(person => (
                  <label key={person.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newMatch.personnel_ids.includes(person.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewMatch({
                            ...newMatch,
                            personnel_ids: [...newMatch.personnel_ids, person.id]
                          });
                        } else {
                          setNewMatch({
                            ...newMatch,
                            personnel_ids: newMatch.personnel_ids.filter(id => id !== person.id)
                          });
                        }
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">{person.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Notlar */}
            <div>
              <Label>Notlar</Label>
              <Textarea
                value={newMatch.notes}
                onChange={(e) => setNewMatch({ ...newMatch, notes: e.target.value })}
                rows={3}
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddMatchModal(false)} className="flex-1">
                İptal
              </Button>
              <Button type="submit" className="flex-1">Maç Ekle</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Team Detail Modal */}
      <Dialog open={showTeamDetailModal} onOpenChange={setShowTeamDetailModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="w-6 h-6 text-blue-500" />
              {selectedTeam?.name} - Detaylar
            </DialogTitle>
          </DialogHeader>
          
          {selectedTeam && (
            <div className="space-y-6">
              {/* Özet */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-gray-600 text-sm">Toplam Maç</p>
                      <p className="text-2xl font-bold">{teamMatches.length}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-gray-600 text-sm">Galibiyet</p>
                      <p className="text-2xl font-bold text-green-600">
                        {teamMatches.filter(m => {
                          if (m.home_score === null) return false;
                          const isHome = m.home_team_id === selectedTeam.id;
                          return isHome ? m.home_score > m.away_score : m.away_score > m.home_score;
                        }).length}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-gray-600 text-sm">Mağlubiyet</p>
                      <p className="text-2xl font-bold text-red-600">
                        {teamMatches.filter(m => {
                          if (m.home_score === null) return false;
                          const isHome = m.home_team_id === selectedTeam.id;
                          return isHome ? m.home_score < m.away_score : m.away_score < m.home_score;
                        }).length}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-gray-600 text-sm">Ödenen</p>
                      <p className="text-2xl font-bold text-blue-600">
                        ₺{teamPayments.filter(p => p.status === 'odendi').reduce((sum, p) => sum + p.amount, 0).toLocaleString()}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Maç Geçmişi */}
              <div>
                <h3 className="text-lg font-bold mb-3">Maç Geçmişi</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {teamMatches.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Henüz maç yok</p>
                  ) : (
                    teamMatches.map(match => {
                      const isHome = match.home_team_id === selectedTeam.id;
                      const opponentId = isHome ? match.away_team_id : match.home_team_id;
                      const opponent = teams.find(t => t.id === opponentId);
                      
                      return (
                        <Card key={match.id}>
                          <CardContent className="py-3">
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="font-semibold">
                                  {isHome ? 'vs' : '@'} {opponent?.name || 'Bilinmiyor'}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {new Date(match.date).toLocaleDateString('tr-TR')} - {match.time}
                                </p>
                                {match.home_score !== null && (
                                  <p className="text-sm font-bold">
                                    Skor: {isHome ? `${match.home_score} - ${match.away_score}` : `${match.away_score} - ${match.home_score}`}
                                  </p>
                                )}
                              </div>
                              <div>
                                {getPaymentStatusBadge(isHome ? match.home_team_payment_status : match.away_team_payment_status)}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Ödeme Geçmişi */}
              <div>
                <h3 className="text-lg font-bold mb-3">Ödeme Geçmişi</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {teamPayments.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Henüz ödeme yok</p>
                  ) : (
                    teamPayments.map((payment, idx) => (
                      <Card key={idx}>
                        <CardContent className="py-3">
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-semibold">{payment.match}</p>
                              <p className="text-sm text-gray-600">
                                {new Date(payment.date).toLocaleDateString('tr-TR')} - {payment.method}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">₺{payment.amount.toLocaleString()}</p>
                              {getPaymentStatusBadge(payment.status)}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Match Detail Modal */}
      <Dialog open={showMatchDetailModal} onOpenChange={setShowMatchDetailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Maç Detayları</DialogTitle>
          </DialogHeader>
          
          {selectedMatch && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-2xl font-bold">
                  {getTeamName(selectedMatch.home_team_id)} vs {getTeamName(selectedMatch.away_team_id)}
                </h3>
                <p className="text-gray-600">
                  {new Date(selectedMatch.date).toLocaleDateString('tr-TR')} - {selectedMatch.time}
                </p>
                <p className="text-gray-600">{getSalonName(selectedMatch.salon_id)}</p>
              </div>

              {/* Skor Güncelleme */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Skor</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 items-center">
                    <Input
                      type="number"
                      placeholder="Ev"
                      defaultValue={selectedMatch.home_score || ''}
                      id="home-score"
                      min="0"
                    />
                    <div className="text-center font-bold">-</div>
                    <Input
                      type="number"
                      placeholder="Deplasman"
                      defaultValue={selectedMatch.away_score || ''}
                      id="away-score"
                      min="0"
                    />
                  </div>
                  <Button
                    className="w-full mt-4"
                    onClick={() => {
                      const homeScore = document.getElementById('home-score').value;
                      const awayScore = document.getElementById('away-score').value;
                      if (homeScore && awayScore) {
                        updateMatchScore(selectedMatch.id, homeScore, awayScore);
                        setShowMatchDetailModal(false);
                      }
                    }}
                  >
                    Skoru Kaydet
                  </Button>
                </CardContent>
              </Card>

              {/* Personel Yönetimi */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Görevli Personel
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Mevcut Personeller */}
                  <div className="space-y-2">
                    {matchPersonnel.length === 0 ? (
                      <p className="text-gray-500 text-sm">Henüz personel atanmamış</p>
                    ) : (
                      matchPersonnel.map((assignment) => (
                        <div key={assignment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-semibold">{assignment.personnel_name}</p>
                            <p className="text-sm text-gray-600">{assignment.role}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => removePersonnelFromMatch(assignment.id, selectedMatch.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Personel Ekleme */}
                  <div className="border-t pt-4">
                    <Label className="mb-2 block">Personel Ekle</Label>
                    <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                      {personnelList
                        .filter(p => !selectedPersonnelIds.includes(p.id))
                        .map(person => (
                          <Button
                            key={person.id}
                            variant="outline"
                            size="sm"
                            className="justify-start"
                            onClick={() => addPersonnelToMatch(selectedMatch.id, person.id)}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            {person.name}
                          </Button>
                        ))}
                      {personnelList.filter(p => !selectedPersonnelIds.includes(p.id)).length === 0 && (
                        <p className="text-sm text-gray-500 col-span-2">Tüm personel atanmış</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Ödemeler */}
              <div className="grid md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Ev Sahibi Ödemesi</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div>Tutar: ₺{selectedMatch.home_team_payment_amount}</div>
                    <div>Durum: {getPaymentStatusBadge(selectedMatch.home_team_payment_status)}</div>
                    {selectedMatch.home_team_payment_status !== 'odendi' && (
                      <Button
                        size="sm"
                        className="w-full mt-2"
                        onClick={() => {
                          updateMatchPayment(selectedMatch.id, 'home', {
                            payment_status: 'odendi',
                            payment_method: 'nakit',
                            payment_amount: selectedMatch.home_team_payment_amount
                          });
                          setShowMatchDetailModal(false);
                        }}
                      >
                        Ödendi Olarak İşaretle
                      </Button>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Deplasman Ödemesi</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div>Tutar: ₺{selectedMatch.away_team_payment_amount}</div>
                    <div>Durum: {getPaymentStatusBadge(selectedMatch.away_team_payment_status)}</div>
                    {selectedMatch.away_team_payment_status !== 'odendi' && (
                      <Button
                        size="sm"
                        className="w-full mt-2"
                        onClick={() => {
                          updateMatchPayment(selectedMatch.id, 'away', {
                            payment_status: 'odendi',
                            payment_method: 'nakit',
                            payment_amount: selectedMatch.away_team_payment_amount
                          });
                          setShowMatchDetailModal(false);
                        }}
                      >
                        Ödendi Olarak İşaretle
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </div>

              {selectedMatch.notes && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Notlar</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700">{selectedMatch.notes}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeagueTab;
